(ns dre.components.searchbar.suggestion-dropdown
  (:require
   [clojure.string :as str]
   [cuerdas.core :as cuerdas]
   [dre.components.icon :as icon]
   [dre.components.searchbar.events :as events]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.config :as config]
   [dre.page-util :as page-util]
   [dre.pages.my-searches.tags.utils :refer [tag-pill]]
   [dre.search-form.render :as sfr]
   [dre.shared.interface.categories :as categories]
   [dre.shared.interface.search.pills :as pills-util]
   [dre.util :as util]
   [dre.utils.bem :as bem-utils]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :suggestion-dropdown)

(defn- be
  [e]
  (bem-utils/be component-name e))

(defn- bem
  [e m]
  (bem-utils/bem component-name e m))

(defn- ->canonical-label
  [{:pill/keys [label canonical-label other-canonical-labels]}]
  (or
   (when-not (= (-> canonical-label util/mlower util/mtrim)
                (-> label util/mlower util/mtrim))
     canonical-label)
   (str/join ", " other-canonical-labels)))

(defn- ->definitions
  [{:pill/keys [definitions]}]
  (when (seq definitions)
    (page-util/truncate
     (cuerdas/strip-tags (str/join "\n\n" (distinct definitions)))
     350
     :hint " [click for more information]")))

(defn- ->classes
  "Generates classes for suggestion row based on pill type"
  [{:pill/keys [type query]} active?]
  [(be :suggestion-row)
   (when active? (bem :suggestion-row :active))

   (if (-> type name (str/starts-with? "regdoc-"))
     (bem :suggestion-row :regdoc)
     (bem :suggestion-row type))

   (when (and
          (= :tag type)
          (-> query keyword namespace seq))
     (bem :suggestion-row (-> query keyword namespace)))

   (when false ; (active? (active-suggestion-idx pill))
     (bem :suggestion-row :active))])

(def ^:private type->icon'
  {:syntax :code
   :range :clock
   :study-type :users
   :category :inbox
   :author :user-doctor})

(def ^:private tag-nmspc->icon
  {"company" :industry
   "country" :globe
   "study-center" :flask
   "gender" :user
   "age" :user
   "race" :user
   "predicted" :magic
   "ethnicity" :user})

(defn- type->icon
  [{:pill/keys [type query]}]
  (or
   (type->icon' type)
   (case type
     :tag
     (-> query keyword namespace tag-nmspc->icon (or :hashtag))
     :source
     (cond
       (str/starts-with? query "http") :rss
       (str/starts-with? query "feeds:") :rss
       (str/starts-with? query "doc-congress") :file-lines
       :else :inbox)
     :study-size :users
     nil)))

(def ^:private category->icon
  {:company :industry
   :country :globe
   :age :user
   :gender :user
   :drugclass :sitemap})

(defn- mode->icon
  [mode]
  (when (and mode
             (not (contains? #{"single-subjects-only"
                               "single-subjects-w-umbrella"} mode)))
        :puzzle-piece))

(defn- ->icon
  [{:pill/keys [category mode] :as pill}]
  (or
   (type->icon pill)
   (category->icon category)
   (mode->icon mode)))

(defn- ->label
  [{:pill/keys [label query mode type] :as pill}
   tags user-profile]
  ;; label (if indent (str "\u21B3" label) label)
  (case type
    :syntax [:b (sfr/syntax-label pill)]
    :range [:<>
            (condp = (:pill/field pill)
              :changed [:i " last changed "]
              :published [:i " published "]
              :approved [:i " approved "])
            (if (sfr/single-year?
                 (:pill/start pill) (:pill/end pill))
              [:b " " [:i " in year "]
               (sfr/nicer-date (:pill/start pill))]
              [:b " "
               (when-let [s (:pill/start pill)]
                 [:i (if (sfr/relative-date? s) " past " " from ")])
               (sfr/nicer-date (:pill/start pill))
               (when-let [s (:pill/end pill)]
                 [:i (if (sfr/relative-date? s) " past " " through ")])
               (sfr/nicer-date (:pill/end pill))])]
    :study-size (let [start (:pill/start pill)
                      end (:pill/end pill)]
                  (if (and start end)
                    (if (= start end)
                      [:b (str " study size = " start)]
                      [:b (str " " start " \u2264 study size \u2264 " end)])
                    [:b (str " study size \u2265 " start)]))
    :tag (let [tn (-> query keyword namespace)
               suffix (sfr/tag-suffix tn)]
           [:b (str " " label
                    (when suffix
                      (str " (" suffix ")")))])
    :utag (let [user-tag (->> tags
                              (filter #(= (:tag_uuid %) query))
                              first)]
            [tag-pill user-tag user-profile {:inline-style? false}])
    :signal [:span
             [icon/pill-fa-icon :fa-save]
             page-util/nbsp
             [:b label]]
    :category (str " " (:label (categories/category-map
                                query)))
    :source [:b (or
                 (:suggestion/label pill)
                 (get (config/source-labels) query)
                 query)]
    :author [:<> "AUTHOR: " (or label query)]
    :text [:<>
           [:i "Search for \""]
           [:span.text query]
           [:i "\" in article text"]]
    [:<> label
     (when (= :withbrandnames mode)
       [:i " with brand names included"])]))

(defn ->title
  [label]
  (cond
    (string? label) label
    (vector? label) (->> label
                         (map ->title)
                         (keep identity)
                         (apply str))
    :else nil))

(defn- ->hint
  [{:pill/keys [type query semgroup-labels]}]
  (cond
    (= :source type) (let [feed? (str/starts-with? query "http")
                           congress? (str/starts-with? query "doc-congress")
                           collection? (str/starts-with? query "collection:")]
                       [:div.pillhint
                        [:div.hint (cond
                                     collection? "collection"
                                     congress? "congress"
                                     feed? "feed")]])
    (seq semgroup-labels) (->> semgroup-labels
                               sort
                               (take 5)
                               (str/join ", "))
    (= :senses type) [:<>
                      [icon/fa-icon :umbrella]
                      "Group of concepts"]))

;; (= :utag type)
    ;; (let [tag (get tag-map query)]
    ;;   [tag-pill tag])

    ;; regdoc?
    ;; [:<>
    ;;  (str " " (or label query))
    ;;  [:<>.pillhint
    ;;   [:<>.hint (case type
    ;;               :regdoc-auth-status "Authorization Status"
    ;;               :regdoc-category "Regulatory Document Type"
    ;;               :regdoc-source "Regulatory Authority"
    ;;               :regdoc-drug-name "Brand Name"
    ;;               :regdoc-substance "Generic Name"
    ;;               :regdoc-therapeutic-area "Disease State"
    ;;               "")]]]

(defn- showing-ellipsis?
  "Determines whether we're showing ellipsis by showing a shorter text or not."
  [el]
  (when el
    (> (.-scrollWidth el)
       (.-clientWidth el))))

(defn- label-component
  [_]
  (let [el (atom nil)
        show-title? (r/atom false)]
    (r/create-class
     {:reagent-render
      (fn [label]
        [:div
         {:ref #(do
                  (reset! el %)
                  (reset! show-title? (showing-ellipsis? %)))
          :class (be :label)
          :title (when @show-title?
                   (->title label))}
         label])})))

(defn- canonical-label-component
  [_]
  (let [el (atom nil)
        show-title? (r/atom false)]
    (r/create-class
     {:reagent-render
      (fn [canonical-label]
        [:div
         {:ref #(do
                  (reset! el %)
                  (reset! show-title? (showing-ellipsis? %)))
          :class (be :canonical-label)
          :title (when @show-title?
                   (->title canonical-label))}
         "(" canonical-label ")"])})))

(defn- hint-component
  [_]
  (let [el (atom nil)
        show-title? (r/atom false)]
    (r/create-class
     {:reagent-render
      (fn [hint]
        [:div
         {:ref #(do
                  (reset! el %)
                  (reset! show-title? (showing-ellipsis? %)))
          :class (be :hint)
          :title (when @show-title?
                   (->title hint))}
         hint])})))

(defn- suggestion-row
  [{:keys [pill id active-suggestion-idx on-click tags user-profile suggestion-tooltip-placement]}]
  (let [definitions (->definitions pill)
        active? (= id @active-suggestion-idx)
        classes (->classes pill active?)
        icon (->icon pill)
        label (->label pill tags user-profile)
        canonical-label (->canonical-label pill)
        hint (->hint pill)]
    [tooltip-wrapper definitions
     [:div
      {:class classes
       :on-mouse-over #(reset! active-suggestion-idx id)
       :on-click on-click}
      (when icon
        [:div
         {:class (be :icon)}
         [icon/fa-icon icon]])
      [label-component label]
      (when-not (str/blank? canonical-label)
        [canonical-label-component canonical-label])
      (when-not (str/blank? hint)
        [hint-component hint])]
     (when suggestion-tooltip-placement {:placement suggestion-tooltip-placement})]))

(defn- suggestion-list
  [{:keys [suggestions] :as opts}]
  [:div
   {:class (be :suggestion-list)}
   (doall
    (map-indexed
     (fn [i suggestion]
       ^{:key i}
       [suggestion-row (assoc opts
                              :pill suggestion
                              :id i)])
     suggestions))])

(defn- suggestion-footer
  [{:keys [db-root-path]}]
  (let [insert-op-pill #(rf/dispatch-sync [::events/insert-pill db-root-path {:pill %}])]
    [:div
     {:class (be :suggestion-footer)}
     [:div
      {:class (be :suggestion-footer-item)
       :on-click #(insert-op-pill pills-util/and-op)}
      "AND"]
     [:div
      {:class (be :suggestion-footer-item)
       :on-click #(insert-op-pill pills-util/or-op)}
      "OR"]
     [:div
      {:class (be :suggestion-footer-item)
       :on-click #(insert-op-pill pills-util/not-op)}
      "NOT"]
     [:div
      {:class (be :suggestion-footer-item)
       :on-click #(insert-op-pill pills-util/lbracket)}
      "("]
     [:div
      {:class (be :suggestion-footer-item)
       :on-click #(insert-op-pill pills-util/rbracket)}
      ")"]]))

(defn suggestion-dropdown
  [{:keys [active-suggestion-idx]}]
  (r/create-class
   {:component-will-unmount #(reset! active-suggestion-idx 0)
    :reagent-render
    (fn [{:keys [db-root-path show-footer? active-suggestion-idx suggestions
                 on-change tags user-profile]
          :or {show-footer? true}
          :as opts}]
      (let [suggestions @suggestions
            tags @tags
            user-profile @user-profile
            opts (assoc opts
                        :suggestions suggestions
                        :tags tags
                        :user-profile user-profile)
            on-click #(do
                        (rf/dispatch-sync [::events/insert-pill
                                           db-root-path
                                           {:pill (nth suggestions @active-suggestion-idx)
                                            :on-change on-change}])
                        (rf/dispatch [::events/clear-suggestions db-root-path]))]
        (when (seq suggestions)
          [:div
           {:class (bem-utils/be :searchbar :suggestion-dropdown)}
           [suggestion-list (assoc opts :on-click on-click)]
           (when show-footer?
             [suggestion-footer opts])])))}))
